import { getApp } from 'firebase/app';
import { ref, getDownloadURL, getStorage } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';
import Konva from 'konva';
import type { SlotTypeBucket } from '../../api/types';

let firebaseStorageInstance: FirebaseStorage | undefined;

function firebaseStorage(): FirebaseStorage {
  if (!firebaseStorageInstance) {
    firebaseStorageInstance = getStorage(getApp());
  }
  return firebaseStorageInstance;
}

// In-memory cache for image URLs
const imageUrlCache = new Map<string, { url: string; timestamp: number }>();
// Deduplicate concurrent URL resolution for the same logical image (e.g. many rack slots).
const typeImageInflight = new Map<string, Promise<string | undefined>>();
// In-memory cache for actual Image objects
interface ImageRef<T> {
  deref: () => T | undefined;
}
type CachedImage = HTMLImageElement | ImageRef<HTMLImageElement>;

const imageCache = new Map<string, { image: CachedImage; timestamp: number }>();

/** URL / decoded-image cache TTL; periodic sweeps use the same interval. */
export const IMAGE_CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_DURATION = IMAGE_CACHE_TTL_MS;

function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

function revokeBlobUrl(url: string): void {
  if (!isBlobUrl(url)) {
    return;
  }
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* noop */
  }
}

function collectBlobUrlsFromImageCache(): string[] {
  return [...imageCache.keys()].filter(isBlobUrl);
}

function getCacheKey(path: string, userId: string, teamId: string | null, bucket: string | null): string {
  return `${path}-${userId}-${teamId || 'null'}-${bucket || 'null'}`;
}

function hasDeref(image: CachedImage): image is ImageRef<HTMLImageElement> {
  return typeof (image as ImageRef<HTMLImageElement>).deref === 'function';
}

function createImageRef(image: HTMLImageElement): CachedImage {
  const weakRefCtor = (globalThis as { WeakRef?: new <T extends object>(value: T) => ImageRef<T> }).WeakRef;
  return weakRefCtor ? new weakRefCtor(image) : image;
}

function getCachedUrl(cacheKey: string): string | null {
  const cached = imageUrlCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.url;
  }
  // Remove expired cache entry
  if (cached) {
    imageUrlCache.delete(cacheKey);
  }
  return null;
}

function setCachedUrl(cacheKey: string, url: string): void {
  imageUrlCache.set(cacheKey, { url, timestamp: Date.now() });
}

function getCachedImage(url: string): HTMLImageElement | null {
  const cached = imageCache.get(url);
  if (!cached) return null;
  if (Date.now() - cached.timestamp >= CACHE_DURATION) {
    imageCache.delete(url);
    return null;
  }
  const img = hasDeref(cached.image) ? cached.image.deref() : cached.image;
  if (!img) {
    imageCache.delete(url);
    revokeBlobUrl(url);
    return null;
  }
  return img as HTMLImageElement;
}

function setCachedImage(url: string, image: HTMLImageElement): void {
  const imgRef = createImageRef(image);
  imageCache.set(url, { image: imgRef, timestamp: Date.now() });
}

/**
 * Clear the image URL cache. Useful when images are updated or when you want to force fresh downloads.
 * Revokes any `blob:` object URLs held in the decoded-image cache (e.g. after destroying a Konva stage).
 */
export function clearImageCache(): void {
  const blobKeys = collectBlobUrlsFromImageCache();
  imageUrlCache.clear();
  imageCache.clear();
  typeImageInflight.clear();
  for (const blobUrl of blobKeys) {
    revokeBlobUrl(blobUrl);
  }
}

/**
 * Clear expired entries from the cache to free up memory.
 */
export function cleanupImageCache(): void {
  const now = Date.now();
  for (const [key, value] of imageUrlCache.entries()) {
    if (now - value.timestamp >= CACHE_DURATION) {
      imageUrlCache.delete(key);
    }
  }
  for (const [key, value] of imageCache.entries()) {
    if (now - value.timestamp >= CACHE_DURATION) {
      imageCache.delete(key);
    }
  }
}

/**
 * Resolve an image URL from the global bucket.
 *
 * @param {string} path - The image path to resolve.
 * @returns {Promise<string>} - The resolved image URL.
 */
export async function populateImage(path: string, fullPath = false): Promise<string> {
  const s = firebaseStorage();
  const globalRef = fullPath ? ref(s, path) : ref(s, `/global/images/${path}`);
  return getDownloadURL(globalRef);
}

/**
 * Resolve an image URL from user, team, or global buckets.
 *
 * @param {string} path - The image path to resolve.
 * @param {string} userId - The current user's ID (required).
 * @param {string|null} teamId - The current team's ID, or null if not in team mode.
 * @param {string|null} bucket - Optional specific bucket to use ('user', 'team', or 'global').
 * @returns {Promise<string>} - The resolved image URL.
*/
async function resolveTypeImageOnce(
  path: string,
  userId: string,
  teamId: string | null,
  bucket: string | null,
  cacheKey: string,
): Promise<string | undefined> {
  try {
    let url: string | undefined;
    let resolvedBucket: string | undefined;
    const cdnTypeUrl = `${import.meta.env.VITE_CDN_URL}global/images/types/${path}`;

    // If a specific bucket is provided, use it immediately
    const s = firebaseStorage();
    if (bucket === 'user') {
      const userRef = ref(s, `user/${userId}/images/types/${path}`);
      url = await getDownloadURL(userRef);
      resolvedBucket = 'user';
    } else if (bucket === 'team' && teamId) {
      const teamRef = ref(s, `teams/${teamId}/images/types/${path}`);
      url = await getDownloadURL(teamRef);
      resolvedBucket = 'team';
    } else if (bucket === 'global') {
      url = cdnTypeUrl;
      resolvedBucket = 'global';
    } else {
      const testResponse = await fetch(cdnTypeUrl).catch(() => null);
      if (testResponse && testResponse.ok) {
        // One fetch; Konva uses blob. URL cache stores HTTPS so later loads skip dead blob: URLs.
        url = URL.createObjectURL(await testResponse.blob());
        resolvedBucket = 'global';
      } else {
        url = undefined;
        console.info('Image not found in global bucket, falling back to team/user bucket.');
      }

      // Fallback logic: check team/user -> global
      if (teamId && !url) {
        const teamRef = ref(s, `teams/${teamId}/images/types/${path}`);
        try {
          url = await getDownloadURL(teamRef);
          resolvedBucket = 'team';
        } catch {
          console.info('Image not found in team bucket, falling back to global bucket.');
        }
      } else if (!url) {
        const userRef = ref(s, `user/${userId}/images/types/${path}`);
        try {
          url = await getDownloadURL(userRef);
          resolvedBucket = 'user';
        } catch {
          console.info('Image not found.');
        }
      }
    }

    if (url && resolvedBucket === 'global') {
      setCachedUrl(cacheKey, cdnTypeUrl);
    } else if (url && resolvedBucket && resolvedBucket !== 'global') {
      setCachedUrl(cacheKey, url);
    }

    return url;
  } catch (error) {
    if (typeof error === 'string') {
      throw new Error(`Failed to resolve image: ${path}. ${error}`);
    } else if (error instanceof Error) {
      throw new Error(`Failed to resolve image: ${path}. ${error.message}`);
    }
  }
}

export async function populateTypeImage(
  path: string,
  userId: string,
  teamId: string|null = null,
  bucket: string|null = null,
): Promise<string|undefined> {
  const cacheKey = getCacheKey(path, userId, teamId, bucket);

  const cachedUrl = getCachedUrl(cacheKey);
  if (cachedUrl) {
    return cachedUrl;
  }

  let inflight = typeImageInflight.get(cacheKey);
  if (!inflight) {
    inflight = resolveTypeImageOnce(path, userId, teamId, bucket, cacheKey).finally(() => {
      typeImageInflight.delete(cacheKey);
    });
    typeImageInflight.set(cacheKey, inflight);
  }

  return inflight;
}

/**
 * Loads images on a Konva stage, applies a condition for image loading,
 * attaches event listeners, and triggers a callback when all images are loaded.
 *
 * @param {Object} stage - The Konva stage instance.
 * @param {Function} loadImagePath - A function that takes an image node and returns the image path for loading.
 * @param {Object} listeners - An object of event listeners to attach to images. Format: { eventName: callback }
 * @param {Function} shouldLoadImage - A function that takes a Konva image node and returns a boolean.
 * @param {Function} onAllImagesLoaded - A callback triggered when all images are fully loaded.
 * @param {Function} [onProgress] - Optional callback for tracking loading progress.
 */
export async function loadStageImages(
  stage: Konva.Stage,
  loadImagePath: (image: Konva.Image) => Promise<string|undefined>,
  listeners = {},
  shouldLoadImage: (image: Konva.Image) => boolean = () => true,
  onAllImagesLoaded: () => void = () => {/* Empty */},
  onProgress: (loaded: number, total: number) => void = () => {/* Empty */},
  onLoadError: (image: Konva.Image, stage: Konva.Stage) => void = () => {/* Empty */},
): Promise<void> {
  // Parameter validation
  if (!stage || typeof stage.find !== 'function') {
    throw new Error('Invalid Konva stage instance');
  }
  if (typeof shouldLoadImage !== 'function') {
    throw new Error('shouldLoadImage must be a function');
  }
  if (typeof loadImagePath !== 'function') {
    throw new Error('loadImagePath must be a function');
  }
  if (typeof listeners !== 'object' || listeners === null) {
    throw new Error('listeners must be an object');
  }
  if (typeof onAllImagesLoaded !== 'function') {
    throw new Error('onAllImagesLoaded must be a function');
  }
  if (onProgress && typeof onProgress !== 'function') {
    throw new Error('onProgress must be a function');
  }

  const images = stage.find('Image') as Konva.Image[]; // Find all image nodes on the stage

  if (images.length === 0) {
    // No images to load, immediately draw the stage and trigger callback
    if (typeof stage.batchDraw === 'function') {
      stage.batchDraw();
    } else {
      const layer = stage.parent;
      if (layer && typeof layer.draw === 'function') {
        layer.draw();
      }
    }
    onAllImagesLoaded();
    return;
  }

  const totalImages = images.length;
  let loadedImages = 0;

  // Update progress and invoke callback when all images are loaded
  const handleImageLoad = () => {
    loadedImages += 1;
    onProgress(loadedImages, totalImages);
  };

  // Process each image node
  const loadImagePromises = images.map(async (image): Promise<void> => {
    // Attach provided event listeners to the image
    for (const [eventName, listener] of Object.entries(listeners)) {
      image.on(eventName, (event) => listener(event, image));
    }

    // Check if the image should be loaded
    if (shouldLoadImage(image)) {
      // Check if the image is already loaded
      if (image.image()) {
        handleImageLoad();
        return Promise.resolve();
      }

      try {
        const imagePath = await loadImagePath(image);

        if (imagePath) {
          // Check if we have a cached image for this URL
          const cachedImage = getCachedImage(imagePath);
          if (cachedImage) {
            image.image(cachedImage);
            handleImageLoad();
            return Promise.resolve();
          }

          // Create new image and load it
          const curImage = new Image();
          curImage.crossOrigin = 'Anonymous';
          curImage.src = imagePath;

          return new Promise((resolve) => {
            const onload = () => {
              // Remove event listeners to prevent memory leaks
              curImage.onload = null;
              curImage.onerror = null;

              // Cache the loaded image
              setCachedImage(imagePath, curImage);
              image.image(curImage);
              handleImageLoad();
              resolve();
            };

            const onerror = () => {
              curImage.onload = null;
              curImage.onerror = null;
              if (isBlobUrl(imagePath)) {
                imageCache.delete(imagePath);
                revokeBlobUrl(imagePath);
              }
              console.error(`Error loading image: ${curImage.src}`);
              handleImageLoad();
              resolve(); // Proceed even if an image fails to load
            };

            curImage.onload = onload;
            curImage.onerror = onerror;
          });
        } else {
          handleImageLoad();
        }
      } catch {
        onLoadError(image, stage);
        handleImageLoad();
      }
    } else {
      handleImageLoad(); // Skip this image
      return Promise.resolve();
    }
  });

  // Wait for all images to finish loading
  await Promise.all(loadImagePromises);

  // Always draw the stage once all images have been processed
  if (typeof stage.batchDraw === 'function') {
    stage.batchDraw();
  } else {
    const layer = stage.parent;
    if (layer && typeof layer.draw === 'function') {
      layer.draw();
    }
  }

  // Trigger the final callback
  onAllImagesLoaded();
}

export async function loadSingleStageImage(
  stage: Konva.Stage,
  objectId: string,
  loadImagePath: (image: Konva.Image) => Promise<string>,
  onImageApplied: (image: Konva.Image) => void = () => {/* Empty */},
): Promise<void> {
  // Parameter validation
  if (!stage || typeof stage.find !== 'function') {
    throw new Error('Invalid Konva stage instance');
  }
  if (typeof objectId === 'undefined') {
    throw new Error('objectId must be provided');
  }
  if (typeof loadImagePath !== 'function') {
    throw new Error('loadImagePath must be a function');
  }
  if (typeof onImageApplied !== 'function') {
    throw new Error('onImageApplied must be a function');
  }

  const images = stage.find('.objectImage') as Konva.Image[]; // Find all image nodes with the class "objectImage"

  // Find the target image node by ID
  const targetImage = images.find((image) => image.attrs.id === objectId);
  if (!targetImage) {
    console.warn(`No image found with ID: ${objectId}`);
    return;
  }

  // Load the new image
  const curImage = new Image();
  curImage.crossOrigin = 'Anonymous';
  curImage.src = await loadImagePath(targetImage);

  return new Promise((resolve) => {
    curImage.onload = () => {
      targetImage.image(curImage); // Apply the loaded image
      // Redraw the stage
      if (typeof stage.batchDraw === 'function') {
        stage.batchDraw();
      } else {
        const layer = stage.parent;
        if (layer && typeof layer.draw === 'function') {
          layer.draw();
        }
      }
      onImageApplied(targetImage); // Trigger callback
      resolve();
    };

    curImage.onerror = () => {
      const src = curImage.src;
      if (isBlobUrl(src)) {
        imageCache.delete(src);
        revokeBlobUrl(src);
      }
      console.error(`Error loading image: ${curImage.src}`);
      resolve(); // Resolve even if loading fails
    };
  });
}

export function getImageBucketByValue(buckets: SlotTypeBucket[], value: string) {
  for (const bucket of buckets) {
    const foundOption = bucket.options.find((option) => option.value === value);
    if (foundOption) {
      return bucket.source; // Return the source of the bucket
    }
  }
  return null; // Return null if the value isn't found in any bucket
}
